// PWA runtime helpers: install-mode detection (for adoption tracking), a
// persistent install id, service-worker registration, and push subscription.

const INSTALL_KEY = "tpfl_install_id";

export function installId(): string {
  let id = localStorage.getItem(INSTALL_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(INSTALL_KEY, id);
  }
  return id;
}

// pwa-standalone = launched from the home screen; pwa-browser = installed-ish
// display but in a tab; web = plain browser. Drives X-TPFL-Client.
export function clientMode(): "pwa-standalone" | "pwa-browser" | "web" {
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  if (standalone) return "pwa-standalone";
  if (window.matchMedia?.("(display-mode: minimal-ui)").matches) return "pwa-browser";
  return "web";
}

export function isStandalone(): boolean {
  return clientMode() === "pwa-standalone";
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.error("[sw] register failed", e);
    return null;
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

// Subscribe to Web Push and register the subscription with the portal. Must be
// called from a user gesture (permission prompt). Returns true on success.
export async function subscribeToPush(vapidPublicKey: string): Promise<boolean> {
  if (!pushSupported() || !vapidPublicKey) return false;
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return false;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource,
    }));
  const { api } = await import("./api");
  await api.post("/api/push/subscribe", { subscription: sub.toJSON(), install: installId() });
  return true;
}
